<template>
  <section class="section-container">
    <h1 class="section-container-heading">
      <p
        v-for="(line, index) in lines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h1>
    <div class="section-container-body fade-up-animation">
      <p>
        This Cookie Policy explains how Vortec ("we", "us", or "our") uses cookies and similar technologies on
        vortec.io. It describes what they are, why we use them, and how you can control your preferences.
      </p>

      <p><strong>Last updated:</strong> February 2025</p>

      <h4 class="heading-4">What Are Cookies</h4>

      <p>
        Cookies are small text files that are placed on your device when you visit a website. They are widely used to
        make websites work efficiently and to provide information to the site owners. Cookies can be "session" cookies
        (deleted when you close your browser) or "persistent" cookies (stored on your device for a set period of time or
        until you delete them).
      </p>

      <h4 class="heading-4">How We Obtain Your Consent</h4>

      <p>
        When you first visit vortec.io, a cookie notice is displayed at the bottom of the page. Non-essential cookies —
        including analytics and behavioral tracking cookies — are <strong>not</strong> loaded until you click "Accept".
        Your consent preference is stored in a cookie named <code>vortecCookies</code> for six months, so you are not
        prompted on every visit. You may withdraw your consent at any time by clearing your browser cookies or
        contacting us.
      </p>

      <h4 class="heading-4">Cookies We Use</h4>

      <p>We use the following categories of cookies on vortec.io:</p>

      <ul style="margin-left: 20px">
        <li>
          <p><strong>Essential cookies</strong></p>
          <p>
            These cookies are necessary for the website to function and cannot be switched off. They do not store any
            personally identifiable information. The <code>vortecCookies</code> cookie falls into this category — it
            records your cookie consent preference and expires after 6 months.
          </p>
        </li>

        <li>
          <p><strong>Form-related cookies</strong></p>
          <p>
            When you submit a form on our website (such as a contact or referral form), a cookie may be used to remember
            your details temporarily, making it easier if you need to re-submit or follow up.
          </p>
        </li>

        <li>
          <p><strong>Analytics cookies (consent required)</strong></p>
          <p>
            These cookies help us understand how visitors interact with our website. They are only activated after you
            accept cookies via our consent notice. The analytics tools we use are:
          </p>
          <ul style="margin-left: 20px; margin-top: 8px">
            <li>
              <strong>Google Analytics</strong> — tracks page visits, session duration, traffic sources, and
              interactions. Data is processed by Google and subject to
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                >Google's Privacy Policy</a
              >.
            </li>
            <li style="margin-top: 6px">
              <strong>ContentSquare</strong> — records behavioral data such as clicks, scrolls, and navigation patterns
              to help us improve the user experience. Data is processed by ContentSquare and subject to their
              <a href="https://contentsquare.com/privacy-center/" target="_blank" rel="noopener noreferrer"
                >Privacy Center</a
              >.
            </li>
          </ul>
        </li>
      </ul>

      <h4 class="heading-4">Third-Party Cookies</h4>

      <p>
        The analytics services listed above (Google Analytics and ContentSquare) may set their own cookies on your
        device when activated. These cookies are governed by the respective privacy policies of those services. Vortec
        does not control these cookies directly.
      </p>

      <h4 class="heading-4">How to Manage Cookies</h4>

      <p>You have several options for managing or disabling cookies:</p>

      <ul style="margin-left: 20px">
        <li>
          <strong>Browser settings</strong> — Most browsers allow you to view, manage, block, or delete cookies through
          their settings. Please refer to your browser's help documentation for specific instructions.
        </li>
        <li>
          <strong>Opt-out tools</strong> — You can opt out of Google Analytics tracking at
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer"
            >tools.google.com/dlpage/gaoptout</a
          >.
        </li>
        <li>
          <strong>Reset your consent</strong> — To withdraw your consent and reset your cookie preference on our site,
          clear the <code>vortecCookies</code> cookie from your browser, and the consent notice will reappear on your
          next visit.
        </li>
      </ul>

      <p>Please note that disabling certain cookies may affect the functionality and user experience of our website.</p>

      <h4 class="heading-4">More Information</h4>

      <p>
        For more information about cookies in general, you can visit
        <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">allaboutcookies.org</a>.
      </p>

      <p>
        If you have any questions about our use of cookies, please contact us at
        <a href="mailto:hello@vortec.io">hello@vortec.io</a>.
      </p>
    </div>
  </section>
  <ClientOnly>
    <ContactForm />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';

const lines = [
  { text: 'Cookie', special: false },
  { text: 'Policy', special: true },
];

useFadeUpObserver();

useSeo({
  title: 'Cookie Policy | VORTEC',
  description: 'Learn about our cookie policy and how we use cookies on our website.',
  path: '/cookie-policy',
  jsonLd: {
    '@type': 'WebPage',
    name: 'Cookie Policy | VORTEC',
    description: 'Learn about our cookie policy and how we use cookies on our website.',
    url: 'https://vortec.io/cookie-policy',
  },
});
</script>
